import React, { useCallback } from 'react';
import { Button, Dropdown, Popover, Select, Tooltip, Upload } from 'antd';
import type { MenuProps } from 'antd';
import {
	AlignCenterOutlined,
	AlignLeftOutlined,
	AlignRightOutlined,
	BgColorsOutlined,
	BoldOutlined,
	DeleteColumnOutlined,
	DeleteOutlined,
	DeleteRowOutlined,
	FontColorsOutlined,
	InsertRowAboveOutlined,
	InsertRowBelowOutlined,
	InsertRowLeftOutlined,
	InsertRowRightOutlined,
	ItalicOutlined,
	LineOutlined,
	LinkOutlined,
	MergeCellsOutlined,
	OrderedListOutlined,
	PictureOutlined,
	RedoOutlined,
	SplitCellsOutlined,
	StrikethroughOutlined,
	TableOutlined,
	UndoOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons';
import type { Editor } from '@tiptap/react';

import type { UploadImageFn } from './inlineImageSupport';

/**
 * Commands registered by extensions (table, text-align, highlight) are not
 * merged onto the chain type in every build, so those calls go through here.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const extChain = (editor: Editor): any => editor.chain().focus();

const COLOR_PALETTE = [
	'#000000',
	'#434343',
	'#666666',
	'#999999',
	'#cccccc',
	'#d93025',
	'#e67c73',
	'#f4511e',
	'#ff6d00',
	'#f09300',
	'#fbbc04',
	'#33b679',
	'#0b8043',
	'#039be5',
	'#1a73e8',
	'#3f51b5',
	'#7b1fa2',
	'#8e24aa',
	'#d81b60',
	'#ad1457',
];

const HIGHLIGHT_PALETTE = [
	'#fef08a',
	'#fde68a',
	'#fed7aa',
	'#fecaca',
	'#fbcfe8',
	'#e9d5ff',
	'#c7d2fe',
	'#bae6fd',
	'#a7f3d0',
	'#bbf7d0',
	'#d9f99d',
	'#fef9c3',
	'#fff1cc',
	'#ffe0b2',
	'#f8bbd0',
];

const HEADING_OPTIONS = [
	{ value: 'paragraph', label: 'Normal text' },
	{ value: '1', label: 'Heading 1' },
	{ value: '2', label: 'Heading 2' },
	{ value: '3', label: 'Heading 3' },
];

function getActiveHeading(editor: Editor): string {
	for (const level of [1, 2, 3] as const) {
		if (editor.isActive('heading', { level })) return String(level);
	}
	return 'paragraph';
}

const ColorGrid: React.FC<{
	colors: string[];
	activeColor?: string;
	onSelect: (color: string) => void;
	onClear: () => void;
	clearLabel: string;
}> = ({ colors, activeColor, onSelect, onClear, clearLabel }) => (
	<div className="rte-swatches">
		<Button size="small" type="text" onClick={onClear} block>
			{clearLabel}
		</Button>
		<div className="rte-swatches__grid">
			{colors.map((color) => (
				<button
					key={color}
					type="button"
					aria-label={color}
					onClick={() => onSelect(color)}
					className="rte-swatch"
					style={{
						background: color,
						outline:
							activeColor?.toLowerCase() === color.toLowerCase()
								? '2px solid #1677ff'
								: 'none',
						outlineOffset: 1,
					}}
				/>
			))}
		</div>
	</div>
);

const ToolbarButton: React.FC<{
	editor: Editor;
	icon: React.ReactNode;
	title: string;
	action: () => void;
	isActive?: boolean;
	disabled?: boolean;
}> = ({ editor, icon, title, action, isActive, disabled }) => (
	<Tooltip title={title}>
		<Button
			className={`rte-tb${isActive ? ' rte-tb--active' : ''}`}
			size="small"
			icon={icon}
			disabled={disabled}
			onClick={() => {
				action();
				editor.commands.focus();
			}}
		/>
	</Tooltip>
);

function getTableMenuItems(editor: Editor): MenuProps['items'] {
	const inTable = editor.isActive('table');
	return [
		{
			key: 'insertTable',
			icon: <TableOutlined />,
			label: 'Insert table',
			disabled: inTable,
		},
		{ type: 'divider' },
		{
			key: 'addColumnBefore',
			icon: <InsertRowLeftOutlined />,
			label: 'Add column before',
			disabled: !inTable,
		},
		{
			key: 'addColumnAfter',
			icon: <InsertRowRightOutlined />,
			label: 'Add column after',
			disabled: !inTable,
		},
		{
			key: 'deleteColumn',
			icon: <DeleteColumnOutlined />,
			label: 'Delete column',
			disabled: !inTable,
		},
		{ type: 'divider' },
		{
			key: 'addRowBefore',
			icon: <InsertRowAboveOutlined />,
			label: 'Add row above',
			disabled: !inTable,
		},
		{
			key: 'addRowAfter',
			icon: <InsertRowBelowOutlined />,
			label: 'Add row below',
			disabled: !inTable,
		},
		{
			key: 'deleteRow',
			icon: <DeleteRowOutlined />,
			label: 'Delete row',
			disabled: !inTable,
		},
		{ type: 'divider' },
		{ key: 'toggleHeaderRow', label: 'Toggle header row', disabled: !inTable },
		{
			key: 'mergeCells',
			icon: <MergeCellsOutlined />,
			label: 'Merge cells',
			disabled: !inTable,
		},
		{
			key: 'splitCell',
			icon: <SplitCellsOutlined />,
			label: 'Split cell',
			disabled: !inTable,
		},
		{ type: 'divider' },
		{
			key: 'deleteTable',
			icon: <DeleteOutlined />,
			label: 'Delete table',
			danger: true,
			disabled: !inTable,
		},
	];
}

function handleTableAction(editor: Editor, key: string) {
	const actions: Record<string, () => void> = {
		insertTable: () =>
			extChain(editor)
				.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
				.run(),
		addColumnBefore: () => extChain(editor).addColumnBefore().run(),
		addColumnAfter: () => extChain(editor).addColumnAfter().run(),
		deleteColumn: () => extChain(editor).deleteColumn().run(),
		addRowBefore: () => extChain(editor).addRowBefore().run(),
		addRowAfter: () => extChain(editor).addRowAfter().run(),
		deleteRow: () => extChain(editor).deleteRow().run(),
		toggleHeaderRow: () => extChain(editor).toggleHeaderRow().run(),
		mergeCells: () => extChain(editor).mergeCells().run(),
		splitCell: () => extChain(editor).splitCell().run(),
		deleteTable: () => extChain(editor).deleteTable().run(),
	};
	actions[key]?.();
}

export interface RichTextToolbarProps {
	editor: Editor | null;
	onUploadImage?: UploadImageFn;
	onInsertImageFile?: (file: File) => void;
}

const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
	editor,
	onUploadImage,
	onInsertImageFile,
}) => {
	const handleSetLink = useCallback(() => {
		if (!editor) return;
		const previous = editor.getAttributes('link').href;
		const url = window.prompt('URL', previous || 'https://');
		if (url === null) return;
		if (url === '') {
			editor.chain().focus().extendMarkRange('link').unsetLink().run();
		} else {
			editor
				.chain()
				.focus()
				.extendMarkRange('link')
				.setLink({ href: url })
				.run();
		}
	}, [editor]);

	const handleHeadingChange = useCallback(
		(value: string) => {
			if (!editor) return;
			if (value === 'paragraph') {
				editor.chain().focus().setParagraph().run();
			} else {
				editor
					.chain()
					.focus()
					.toggleHeading({ level: Number(value) as 1 | 2 | 3 })
					.run();
			}
		},
		[editor]
	);

	if (!editor) return null;

	return (
		<div className="rte-toolbar">
			<Select
				className="rte-heading-select"
				size="small"
				variant="borderless"
				value={getActiveHeading(editor)}
				onChange={handleHeadingChange}
				options={HEADING_OPTIONS}
				popupMatchSelectWidth={false}
			/>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<ToolbarButton
					editor={editor}
					title="Bold (Ctrl+B)"
					icon={<BoldOutlined />}
					isActive={editor.isActive('bold')}
					action={() => editor.chain().toggleBold().run()}
				/>
				<ToolbarButton
					editor={editor}
					title="Italic (Ctrl+I)"
					icon={<ItalicOutlined />}
					isActive={editor.isActive('italic')}
					action={() => editor.chain().toggleItalic().run()}
				/>
				<ToolbarButton
					editor={editor}
					title="Strikethrough"
					icon={<StrikethroughOutlined />}
					isActive={editor.isActive('strike')}
					action={() => editor.chain().toggleStrike().run()}
				/>
			</div>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<Popover
					trigger="click"
					placement="bottom"
					content={
						<ColorGrid
							colors={COLOR_PALETTE}
							activeColor={editor.getAttributes('textStyle').color}
							onSelect={(color) => extChain(editor).setColor(color).run()}
							onClear={() => extChain(editor).unsetColor().run()}
							clearLabel="Default color"
						/>
					}
				>
					<Tooltip title="Text color">
						<Button
							className="rte-tb"
							size="small"
							icon={
								<FontColorsOutlined
									style={{
										borderBottom: `3px solid ${
											editor.getAttributes('textStyle').color || 'currentColor'
										}`,
										paddingBottom: 1,
									}}
								/>
							}
						/>
					</Tooltip>
				</Popover>

				<Popover
					trigger="click"
					placement="bottom"
					content={
						<ColorGrid
							colors={HIGHLIGHT_PALETTE}
							activeColor={editor.getAttributes('highlight').color}
							onSelect={(color) =>
								extChain(editor).toggleHighlight({ color }).run()
							}
							onClear={() => extChain(editor).unsetHighlight().run()}
							clearLabel="No highlight"
						/>
					}
				>
					<Tooltip title="Highlight color">
						<Button
							className={`rte-tb${
								editor.isActive('highlight') ? ' rte-tb--active' : ''
							}`}
							size="small"
							icon={
								<BgColorsOutlined
									style={{
										borderBottom: `3px solid ${
											editor.getAttributes('highlight').color || '#fef08a'
										}`,
										paddingBottom: 1,
									}}
								/>
							}
						/>
					</Tooltip>
				</Popover>
			</div>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<ToolbarButton
					editor={editor}
					title="Align left"
					icon={<AlignLeftOutlined />}
					isActive={editor.isActive({ textAlign: 'left' })}
					action={() => extChain(editor).setTextAlign('left').run()}
				/>
				<ToolbarButton
					editor={editor}
					title="Align center"
					icon={<AlignCenterOutlined />}
					isActive={editor.isActive({ textAlign: 'center' })}
					action={() => extChain(editor).setTextAlign('center').run()}
				/>
				<ToolbarButton
					editor={editor}
					title="Align right"
					icon={<AlignRightOutlined />}
					isActive={editor.isActive({ textAlign: 'right' })}
					action={() => extChain(editor).setTextAlign('right').run()}
				/>
			</div>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<ToolbarButton
					editor={editor}
					title="Bullet list"
					icon={<UnorderedListOutlined />}
					isActive={editor.isActive('bulletList')}
					action={() => editor.chain().toggleBulletList().run()}
				/>
				<ToolbarButton
					editor={editor}
					title="Numbered list"
					icon={<OrderedListOutlined />}
					isActive={editor.isActive('orderedList')}
					action={() => editor.chain().toggleOrderedList().run()}
				/>
			</div>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<ToolbarButton
					editor={editor}
					title="Horizontal rule"
					icon={<LineOutlined />}
					action={() => editor.chain().setHorizontalRule().run()}
				/>
				<ToolbarButton
					editor={editor}
					title="Link"
					icon={<LinkOutlined />}
					isActive={editor.isActive('link')}
					action={handleSetLink}
				/>
				{onUploadImage && (
					<Upload
						accept="image/*"
						showUploadList={false}
						beforeUpload={(file) => {
							onInsertImageFile?.(file);
							return false;
						}}
					>
						<Tooltip title="Insert image">
							<Button
								className="rte-tb"
								size="small"
								icon={<PictureOutlined />}
							/>
						</Tooltip>
					</Upload>
				)}
			</div>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<Dropdown
					menu={{
						items: getTableMenuItems(editor),
						onClick: ({ key }) => handleTableAction(editor, key),
					}}
					trigger={['click']}
				>
					<Tooltip title="Table">
						<Button
							className={`rte-tb${
								editor.isActive('table') ? ' rte-tb--active' : ''
							}`}
							size="small"
							icon={<TableOutlined />}
						/>
					</Tooltip>
				</Dropdown>
			</div>

			<div className="rte-toolbar__divider" />

			<div className="rte-toolbar__group">
				<ToolbarButton
					editor={editor}
					title="Undo (Ctrl+Z)"
					icon={<UndoOutlined />}
					action={() => editor.chain().undo().run()}
					disabled={!editor.can().undo()}
				/>
				<ToolbarButton
					editor={editor}
					title="Redo (Ctrl+Y)"
					icon={<RedoOutlined />}
					action={() => editor.chain().redo().run()}
					disabled={!editor.can().redo()}
				/>
			</div>
		</div>
	);
};

export default RichTextToolbar;
